import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../api/axiosConfig';

const FacilityManagePage = () => {
  const { id: facilityId } = useParams();
  const navigate = useNavigate();
  const [facility, setFacility] = useState(null);
  const [sections, setSections] = useState([]);
  const [initialSections, setInitialSections] = useState([]);
  const [coverage, setCoverage] = useState(null);
  const [error, setError] = useState('');

  /** 이 구역을 도는 계획들. 아직 안 왔으면 빈 목록이 아니라 null 로 구분해야
      '없음' 과 '아직 모름' 이 섞이지 않는다. */
  const coverageOf = (sectionId) =>
    coverage?.sections?.find((c) => c.id === sectionId)?.coveredBy ?? [];
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.get(`/facilities/${facilityId}`);
        // 이 구역들을 무엇이 도는지. 예전에는 구역이 점검표를 하나 들고 있었지만
        // 이제는 계획의 정거장에 붙으므로 계획 쪽에 물어야 한다.
        api
          .get(`/inspection-plans/coverage/facility/${facilityId}`)
          .then((res) => setCoverage(res.data))
          .catch((err) => console.error('Failed to fetch coverage', err));
        setFacility(response.data);
        // Sort sections by order just in case they aren't already
        const sortedSections = response.data.sections.sort((a, b) => a.order - b.order);
        setSections(sortedSections);
        setInitialSections(sortedSections);
      } catch (err) {
        setError('정보를 불러오는데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [facilityId]);

  const isOrderChanged = useMemo(() => {
    if (initialSections.length !== sections.length) return true;
    return JSON.stringify(initialSections.map(s => s.id)) !== JSON.stringify(sections.map(s => s.id));
  }, [initialSections, sections]);

  const handleMove = (index, direction) => {
    const newSections = [...sections];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    if (targetIndex < 0 || targetIndex >= newSections.length) {
      return;
    }

    // Swap elements
    [newSections[index], newSections[targetIndex]] = [newSections[targetIndex], newSections[index]];
    setSections(newSections);
  };

  const handleDelete = async (sectionId) => {
    if (window.confirm('정말로 이 구역을 삭제하시겠습니까?')) {
      try {
        await api.delete(`/sections/${sectionId}`);
        const newSections = sections.filter(s => s.id !== sectionId);
        setSections(newSections);
        setInitialSections(newSections); // Also update initial state
      } catch (err) {
        setError('구역 삭제에 실패했습니다.');
      }
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');
    try {
      const reorderPayload = {
        items: sections.map((section, index) => ({
          id: section.id,
          order: index,
        })),
      };
      await api.patch('/sections/reorder', reorderPayload);
      navigate(`/facilities/${facilityId}`);
    } catch (err) {
      setError('순서 저장에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !facility) {
    return <div className="container mt-4 text-center">로딩 중...</div>;
  }

  if (error) {
    return <div className="container mt-4 alert alert-danger">{error}</div>;
  }

  return (
    <div className="container">
      <header className="d-flex justify-content-between align-items-center pb-3 mb-3">
        <button className="btn p-0 border-0 me-2" onClick={() => navigate(-1)}>
          <i className="bi bi-arrow-left fs-4"></i>
        </button>
        <h4 className="mb-0 fw-bold">구역 관리</h4>
        <button type="button" className="btn btn-primary" onClick={handleSave} disabled={!isOrderChanged || loading}>
          {loading ? '저장중...' : '저장'}
        </button>
      </header>

      <div className="card p-3">
        <p className="text-muted small">
          위/아래 버튼을 눌러 구역 순서를 바꿀 수 있습니다.
        </p>
        <ul className="list-group list-group-flush">
          {sections.map((section, index) => (
            <li key={section.id} className="list-group-item d-flex justify-content-between align-items-center">
              <div style={{ minWidth: 0 }}>
                <div className="fw-bold mb-1">{section.name}</div>
                {/* 어느 계획이 이 구역을 어떤 점검표로 도는지. 계획과 점검표를
                    '›' 로 이어 두면 어느 쪽이 무엇인지 이름만 보고도 갈린다 --
                    점검표 이름이 구역 이름과 비슷해서 나란히 두면 구분이 안 된다. */}
                {/* 아직 안 왔을 때와 정말 없을 때를 갈라야 한다. 오는 사이에
                    붉은 경고가 번쩍이면 놀라기만 하고 알려주는 것이 없다. */}
                {coverage === null ? (
                  <span className="text-muted small">불러오는 중…</span>
                ) : coverageOf(section.id).length === 0 ? (
                  <span className="badge bg-danger bg-opacity-10 text-danger border border-danger-subtle rounded-pill">
                    <i className="bi bi-exclamation-triangle me-1"></i>
                    어느 순회에도 들어 있지 않음
                  </span>
                ) : (
                  coverageOf(section.id).map((c, i) => (
                    <div key={`${c.planId}-${i}`} className="text-muted small">
                      <i className="bi bi-calendar-check me-1"></i>
                      {c.planName}
                      {c.planStatus === 'PAUSED' && ' (멈춤)'}
                      <span className="mx-1">›</span>
                      {c.templateName ?? <span className="text-danger">점검표 없음</span>}
                    </div>
                  ))
                )}
              </div>
              <div className="d-flex align-items-center">
                <button className="btn btn-light btn-sm me-1" title="위로 이동" onClick={() => handleMove(index, 'up')} disabled={index === 0}>
                  <i className="bi bi-arrow-up"></i>
                </button>
                <button className="btn btn-light btn-sm me-2" title="아래로 이동" onClick={() => handleMove(index, 'down')} disabled={index === sections.length - 1}>
                  <i className="bi bi-arrow-down"></i>
                </button>
                <Link to={`/facilities/${facilityId}/sections/${section.id}/edit`} className="btn btn-outline-primary btn-sm me-1" title="수정">
                  <i className="bi bi-pencil-square"></i>
                </Link>
                <button className="btn btn-outline-danger btn-sm" title="삭제" onClick={() => handleDelete(section.id)}>
                  <i className="bi bi-trash"></i>
                </button>
              </div>
            </li>
          ))}
        </ul>
        <div className="mt-3">
          <Link to={`/facilities/${facilityId}/sections/new`} className="btn btn-outline-primary w-100">
            + 구역 추가
          </Link>
        </div>
      </div>
    </div>
  );
};

export default FacilityManagePage;